import { z } from 'zod';
import { DOCUMENT_STATUSES, ROLES } from '../enums.js';
import {
  dateRangeQuery,
  dayString,
  departmentRef,
  employeeRef,
  isoDateTime,
  listQueryWith,
  metric,
  nonEmpty,
  objectId,
  scopeQuery,
  timestamps,
} from './common.js';

/** Documents, onboarding, analytics, settings and audit. */

// ── Employee documents ──────────────────────────────────────────────────────────

export const employeeDocument = z
  .object({
    id: objectId,
    employee: employeeRef,
    requirementCode: z.string(),
    name: z.string(),
    required: z.boolean(),
    status: z.enum(DOCUMENT_STATUSES),
    fileId: objectId.nullable(),
    originalFilename: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    collectedBy: employeeRef.nullable(),
    collectedAt: isoDateTime.nullable(),
    verifiedBy: employeeRef.nullable(),
    verifiedAt: isoDateTime.nullable(),
    rejectedReason: z.string().nullable(),
    expiryDate: dayString.nullable(),
    isExpiringSoon: z.boolean(),
  })
  .merge(timestamps);

export const documentChecklist = z.object({
  employee: employeeRef,
  documents: z.array(employeeDocument),
  stats: z.object({
    required: z.number().int(),
    verified: z.number().int(),
    outstanding: z.number().int(),
  }),
});

export const rejectDocumentBody = z.object({
  reason: nonEmpty('Rejection reason', 500),
});

export const verifyDocumentBody = z.object({
  expiryDate: dayString.optional(),
  note: z.string().max(500).optional(),
});

/**
 * Upload metadata.
 *
 * The FILE itself is the raw request body, with its name in `x-file-name` (ADR-020), so
 * these fields travel in the query string rather than in a JSON body. The server decides
 * the file's type by inspecting its CONTENT — never from the extension, and never from
 * anything the client asserts here.
 */
export const uploadDocumentBody = z.object({
  expiryDate: dayString.optional(),
});

export const documentListQuery = listQueryWith({
  status: z.enum(DOCUMENT_STATUSES).optional(),
  expiringWithinDays: z.coerce.number().int().min(1).max(365).optional(),
});

// ── Onboarding ──────────────────────────────────────────────────────────────────

export const onboardingItem = z.object({
  code: z.string(),
  label: z.string(),
  ownerRole: z.enum(ROLES).nullable(),
  dueDate: dayString.nullable(),
  completed: z.boolean(),
  completedBy: employeeRef.nullable(),
  completedAt: isoDateTime.nullable(),
});

export const onboardingChecklist = z
  .object({
    id: objectId,
    employee: employeeRef,
    items: z.array(onboardingItem),
    progressPercent: z.number().int().min(0).max(100),
    status: z.enum(['not_started', 'in_progress', 'complete']),
  })
  .merge(timestamps);

export const updateOnboardingItemBody = z.object({
  completed: z.boolean(),
  note: z.string().max(500).optional(),
});

export const confirmProbationBody = z.object({
  confirmedOn: dayString,
  notes: z.string().max(1000).optional(),
});

// ── Analytics ───────────────────────────────────────────────────────────────────

/**
 * Dashboard payload.
 *
 * Every figure is a `metric`, which carries whether it is observed or derived and — for
 * a derived one — the basis. The UI cannot present an estimate as a measurement without
 * deliberately discarding that field.
 */
export const dashboardPayload = z.object({
  generatedAt: isoDateTime,
  scope: z.enum(['own', 'reports', 'department', 'organization']),
  metrics: z.record(z.string(), metric),
  series: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
        kind: z.enum(['observed', 'derived']),
        points: z.array(z.object({ date: dayString, value: z.number() })),
      }),
    )
    .default([]),
  /**
   * Breakdowns that have no time axis — headcount by department, say.
   *
   * Separate from `series` because forcing a distribution into a time-series shape
   * means inventing dates for it, and an invented value is exactly what §L forbids.
   */
  distribution: z
    .array(z.object({ key: z.string(), label: z.string(), value: z.number() }))
    .default([]),
});

export const analyticsQuery = z.object({
  ...dateRangeQuery.shape,
  ...scopeQuery.shape,
  departmentId: objectId.optional(),
  groupBy: z.enum(['day', 'week', 'month', 'department', 'employee']).optional(),
});

export const exportQuery = z.object({
  ...dateRangeQuery.shape,
  ...scopeQuery.shape,
  format: z.enum(['csv']).default('csv'),
});

// ── Settings ────────────────────────────────────────────────────────────────────

export const organizationSettings = z.object({
  legalName: z.string().nullable(),
  displayName: z.string().nullable(),
  logoFileId: objectId.nullable(),
  website: z.string().nullable(),
  supportEmail: z.string().nullable(),
});

export const systemPreferences = z.object({
  timezone: z.string(),
  weekStartsOn: z.number().int().min(0).max(6),
});

/** The password is write-only and always masked on read. */
export const emailSettings = z.object({
  host: z.string().nullable(),
  port: z.number().int().nullable(),
  user: z.string().nullable(),
  passwordSet: z.boolean(),
  from: z.string().nullable(),
  enabled: z.boolean(),
});

export const updateEmailSettingsBody = z.object({
  host: z.string().max(200),
  port: z.number().int().min(1).max(65535),
  user: z.string().max(200).optional(),
  password: z.string().max(400).optional(),
  from: z.string().email(),
});

export const companySettings = z.object({
  organization: organizationSettings,
  systemPreferences,
  email: emailSettings,
});

export const testEmailBody = z.object({ to: z.string().email() });

/** Read-only: roles are code, not runtime configuration. */
export const permissionMatrix = z.object({
  roles: z.array(
    z.object({
      role: z.enum(ROLES),
      rank: z.number().int(),
      permissions: z.array(z.string()),
    }),
  ),
  scopeGrants: z.object({
    manager: z.array(z.string()),
    departmentHead: z.array(z.string()),
  }),
});

// ── Audit ───────────────────────────────────────────────────────────────────────

export const auditEntry = z.object({
  id: objectId,
  actor: z.object({ userId: objectId.nullable(), email: z.string().nullable() }),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  summary: z.record(z.string(), z.unknown()).nullable(),
  ipAddress: z.string().nullable(),
  requestId: z.string().nullable(),
  outcome: z.enum(['success', 'denied']),
  createdAt: isoDateTime,
});

export const auditListQuery = listQueryWith({
  ...dateRangeQuery.shape,
  actorUserId: objectId.optional(),
  resourceType: z.string().max(60).optional(),
  action: z.string().max(80).optional(),
  outcome: z.enum(['success', 'denied']).optional(),
});

// ── Portal activity (admin) ─────────────────────────────────────────────────────

export const portalActivityListQuery = listQueryWith({
  ...dateRangeQuery.shape,
});

export const portalActivitySummary = z.object({
  activeUsersNow: z.number().int(),
  signInsInRange: z.number().int(),
  totalPortalMinutesInRange: z.number().int(),
  from: isoDateTime,
  to: isoDateTime,
});

export const portalActivityUserRow = z.object({
  userId: objectId,
  email: z.string(),
  role: z.enum(ROLES),
  employee: employeeRef.nullable(),
  activeNow: z.boolean(),
  activeSessionCount: z.number().int(),
  lastSeenAt: isoDateTime.nullable(),
  portalMinutesInRange: z.number().int(),
  lastSignInAt: isoDateTime.nullable(),
});

export const portalActivitySignIn = z.object({
  id: objectId,
  email: z.string().nullable(),
  employee: employeeRef.nullable(),
  ipAddress: z.string().nullable(),
  createdAt: isoDateTime,
});

export const portalActivitySession = z.object({
  id: objectId,
  userId: objectId,
  email: z.string(),
  employee: employeeRef.nullable(),
  startedAt: isoDateTime,
  lastSeenAt: isoDateTime,
  endedAt: isoDateTime.nullable(),
  durationMinutes: z.number().int(),
  active: z.boolean(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
});

export const portalActivityPayload = z.object({
  summary: portalActivitySummary,
  recentSignIns: z.array(portalActivitySignIn),
  sessions: z.array(portalActivitySession),
  users: z.array(portalActivityUserRow),
});

// ── App config and health ───────────────────────────────────────────────────────

export const health = z.object({
  status: z.enum(['ok', 'degraded']),
  db: z.enum(['up', 'down']),
  uptimeSeconds: z.number().int(),
});

/** Public. Carries the Web Push PUBLIC key only — never the private one. */
export const appConfig = z.object({
  features: z.object({
    push: z.boolean(),
    email: z.boolean(),
    desktop: z.boolean(),
  }),
  vapidPublicKey: z.string().nullable(),
  organizationName: z.string().nullable(),
});

export const myTeamMember = z.object({
  employee: employeeRef,
  department: departmentRef.nullable(),
  attendanceToday: z.enum(['present', 'absent', 'on_leave', 'holiday', 'not_started']),
  openLeaveRequests: z.number().int(),
  openTasks: z.number().int(),
});
